import React from 'react';
import { Helmet } from 'react-helmet';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useApplicationTracking } from '../hooks/useApplicationTracking';

const STEPS = [
  'Vahvistamme hakemuksesi tiedot',
  'Haemme tarjouksia useilta rahoittajilta',
  'Olemme sinuun yhteydessä',
];

function activeStepIndex(status) {
  if (status === 'ClosedApplication') return 3;
  if (status === 'InProcessApplication') return 2;
  return 1;
}

export default function MobileApplicationStatus() {
  const { token: rawToken } = useParams();
  const token = rawToken ? decodeURIComponent(rawToken).toUpperCase() : '';
  const { data, loading, error } = useApplicationTracking(token);
  const currentStep = activeStepIndex(data?.status);

  return (
    <>
      <Helmet>
        <title>Hakemuksen tila | Rahoituskanava</title>
      </Helmet>
      <Box sx={{ px: 2, py: 3, flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
          }}
        >
          <Typography variant="h1" gutterBottom>
            Hakemuksen tila
          </Typography>

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {!loading && error && (
            <>
              <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                {error}
              </Typography>
              <Button component={RouterLink} to="/m" variant="contained" fullWidth>
                Palaa hakemukseen
              </Button>
            </>
          )}

          {!loading && data && (
            <>
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  bgcolor: 'mobile.highlight',
                  borderRadius: 2,
                  textAlign: 'center',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Hakemustunnus
                </Typography>
                <Typography variant="h2" sx={{ fontSize: '1.35rem', letterSpacing: 0.5 }}>
                  {data.trackingToken}
                </Typography>
                <Typography variant="body1" sx={{ mt: 1.5, fontWeight: 600 }}>
                  {data.statusLabel}
                </Typography>
              </Box>

              <Typography variant="body1" sx={{ fontWeight: 600, mb: 1.5 }}>
                Mitä tapahtuu seuraavaksi
              </Typography>
              <Box
                component="ol"
                sx={{
                  pl: 2.25,
                  mb: 3,
                  color: 'text.primary',
                  '& li': { mb: 0.75, pl: 0.5 },
                }}
              >
                {STEPS.map((label, index) => {
                  const stepNumber = index + 1;
                  const isDone = stepNumber < currentStep;
                  const isCurrent = stepNumber === currentStep && data.status !== 'ClosedApplication';

                  return (
                    <Typography
                      key={label}
                      component="li"
                      variant="body1"
                      sx={{
                        fontWeight: isCurrent ? 600 : 400,
                        color: isDone || isCurrent ? 'text.primary' : 'text.secondary',
                      }}
                    >
                      {label}
                      {isDone ? ' ✓' : null}
                    </Typography>
                  );
                })}
              </Box>

              <Button component={RouterLink} to="/m" variant="contained" color="primary" fullWidth>
                Palaa etusivulle
              </Button>
            </>
          )}
        </Paper>
      </Box>
    </>
  );
}
